import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, RotateCw, X } from 'lucide-react';

export type BannerKind = 'info' | 'success' | 'warning' | 'danger';

interface ErrorBannerProps {
  kind?: BannerKind;
  title?: string;
  message: React.ReactNode;
  /** When provided, renders a retry button. */
  onRetry?: () => void;
  retryLabel?: string;
  /** When provided, renders a dismiss button. */
  onDismiss?: () => void;
  /** Compact one-line layout (no separate title). */
  inline?: boolean;
}

const ICONS: Record<BannerKind, typeof AlertCircle> = {
  info:    Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger:  AlertCircle
};

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  kind = 'danger',
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  onDismiss,
  inline = false
}) => {
  const Icon = ICONS[kind];
  return (
    <div
      className={`hf-banner ${kind} ${inline ? 'hf-banner-inline' : ''}`}
      role={kind === 'danger' || kind === 'warning' ? 'alert' : 'status'}
    >
      <Icon size={inline ? 14 : 16} strokeWidth={2} className={`hf-banner-icon ${kind}`} />
      <div className="hf-banner-text">
        {title && !inline && <div className="hf-banner-title">{title}</div>}
        <div className="hf-banner-msg">{message}</div>
      </div>
      {onRetry && (
        <button type="button" className="hf-btn sm ghost" onClick={onRetry}>
          <RotateCw size={12} /> {retryLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          className="hf-banner-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};
